import { z } from "zod";

import { ContactSearchQueryVariables } from "lib/generated/app-service-gql/graphql";

type ContactSearchGqlInput = ContactSearchQueryVariables["input"];

/**
 * Filter selections for the Cognism contacts table. Persisted to the URL (see
 * `useURLState` in CognismContactsTable), so the shape is described with Zod and
 * every field carries a default — a partial/old URL still parses cleanly.
 *
 * `searchQuery` is a unified name box. `hasEmail` ANDs with the phone group. The two phone
 * types are children of a "Has phone number" parent and OR together (like CPV / buyer
 * types), so both ticked = "has any phone" — see {@link buildContactSearchInput}. The
 * parent holds no state of its own; it is derived via {@link phoneParentChecked}.
 */
export const CognismContactFiltersSchema = z.object({
  searchQuery: z.string().default(""),
  hasEmail: z.boolean().default(false),
  hasMobilePhone: z.boolean().default(false),
  hasDirectPhone: z.boolean().default(false),
  // Buyer filter: individual buyers / saved lists / buyer types.
  buyerGuids: z.array(z.string()).default([]),
  buyerListIds: z.array(z.string()).default([]),
  buyerCategoryIds: z.array(z.string()).default([]),
  // Unified seniority tier keys (labels are backend-served via seniorityTierOptions).
  seniorityTiers: z.array(z.string()).default([]),
  // Unified job-function slugs (labels are backend-served via jobFunctionOptions).
  jobFunctions: z.array(z.string()).default([]),
  // Free-text job-title keywords (matched server-side by the shared keyword matcher).
  jobTitles: z.array(z.string()).default([]),
});

export type CognismContactFilters = z.infer<typeof CognismContactFiltersSchema>;

export const EMPTY_CONTACT_FILTERS: CognismContactFilters = {
  searchQuery: "",
  hasEmail: false,
  hasMobilePhone: false,
  hasDirectPhone: false,
  buyerGuids: [],
  buyerListIds: [],
  buyerCategoryIds: [],
  seniorityTiers: [],
  jobFunctions: [],
  jobTitles: [],
};

/** True when either phone type is selected, i.e. the phone group constrains the search. */
export function hasPhoneFilter(f: CognismContactFilters): boolean {
  return f.hasMobilePhone || f.hasDirectPhone;
}

/**
 * Tri-state for the "Has phone number" parent: checked when both types are selected
 * (which the query treats as "any phone"), indeterminate when exactly one is.
 */
export function phoneParentChecked(f: CognismContactFilters): boolean | "indeterminate" {
  if (f.hasMobilePhone && f.hasDirectPhone) return true;
  if (hasPhoneFilter(f)) return "indeterminate";
  return false;
}

/** Clicking the parent selects both phone types, or clears both when already fully checked. */
export function togglePhoneParent(f: CognismContactFilters): CognismContactFilters {
  const selectAll = phoneParentChecked(f) !== true;
  return { ...f, hasMobilePhone: selectAll, hasDirectPhone: selectAll };
}

/**
 * Count of active filters, shown in the sidebar header and on the collapsed-sidebar funnel
 * button. The phone group counts once however many types are ticked — parent plus children
 * is one constraint on the search, not two.
 */
export function countActiveFilters(f: CognismContactFilters): number {
  return (
    f.buyerGuids.length +
    f.buyerListIds.length +
    f.buyerCategoryIds.length +
    f.seniorityTiers.length +
    f.jobFunctions.length +
    f.jobTitles.length +
    (f.searchQuery.trim() ? 1 : 0) +
    (f.hasEmail ? 1 : 0) +
    (hasPhoneFilter(f) ? 1 : 0)
  );
}

/** Accordion sections in the contacts filter sidebar, mirroring the legacy Oscar layout. */
export type ContactFilterGroupKey = "role" | "contactInformation" | "buyers" | "contactName";

/** All filter sections, in display order. */
export const CONTACT_FILTER_GROUP_KEYS: ContactFilterGroupKey[] = [
  "role",
  "contactInformation",
  "buyers",
  "contactName",
];

/** Per-section active-filter count, for the collapse header badge. */
export const CONTACT_FILTER_GROUP_COUNTS: Record<
  ContactFilterGroupKey,
  (f: CognismContactFilters) => number
> = {
  // "role" groups job title + seniority + job function.
  role: (f) => f.jobTitles.length + f.seniorityTiers.length + f.jobFunctions.length,
  contactInformation: (f) => (f.hasEmail ? 1 : 0) + (hasPhoneFilter(f) ? 1 : 0),
  buyers: (f) => f.buyerGuids.length + f.buyerListIds.length + f.buyerCategoryIds.length,
  contactName: (f) => (f.searchQuery.trim() ? 1 : 0),
};

/** Fields to reset when a section's clear "x" is clicked. */
export const CONTACT_FILTER_GROUP_RESETS: Record<
  ContactFilterGroupKey,
  Partial<CognismContactFilters>
> = {
  role: { jobTitles: [], seniorityTiers: [], jobFunctions: [] },
  contactInformation: { hasEmail: false, hasMobilePhone: false, hasDirectPhone: false },
  buyers: { buyerGuids: [], buyerListIds: [], buyerCategoryIds: [] },
  contactName: { searchQuery: "" },
};

/** Active-filter count across a subset of sections (embedded surfaces hide some). */
export function countActiveFiltersForGroups(
  filters: CognismContactFilters,
  groups: ContactFilterGroupKey[],
): number {
  return groups.reduce((sum, group) => sum + CONTACT_FILTER_GROUP_COUNTS[group](filters), 0);
}

/**
 * Maps the phone group to the backend's presence flags. The two types are children of one
 * parent and OR together, so ticking both is "has any phone" (`hasAnyPhone`), not "has both
 * types" — the backend flags AND, so sending both would be a much narrower search
 * (only contacts carrying both a mobile and a direct number).
 */
function phoneSearchInput(f: CognismContactFilters): Partial<ContactSearchGqlInput> {
  if (f.hasMobilePhone && f.hasDirectPhone) return { hasAnyPhone: true };
  if (f.hasMobilePhone) return { hasMobilePhone: true };
  if (f.hasDirectPhone) return { hasDirectPhone: true };
  return {};
}

/**
 * Builds the `contactSearch` input from filter state, omitting empty filters so an
 * unfiltered search keeps minimal, stable query variables (shared with export, so
 * "export all" always matches the visible search).
 *
 * `lockedBuyerGuids` (embedded buyer scope) replaces the user's buyer filters wholesale:
 * the backend UNIONS list members with buyer guids, so stale list/category ids from URL
 * state would silently widen a locked surface — and its export — beyond the buyer.
 */
export function buildContactSearchInput(
  filters: CognismContactFilters,
  page: { first: number; after: string | null; lockedBuyerGuids?: string[] },
): ContactSearchGqlInput {
  const searchQuery = filters.searchQuery.trim();
  const locked = !!page.lockedBuyerGuids?.length;
  return {
    first: page.first,
    after: page.after,
    ...(searchQuery ? { searchQuery } : {}),
    ...(filters.hasEmail ? { hasEmail: true } : {}),
    ...phoneSearchInput(filters),
    ...(locked
      ? { buyerGuids: page.lockedBuyerGuids }
      : {
          ...(filters.buyerGuids.length ? { buyerGuids: filters.buyerGuids } : {}),
          ...(filters.buyerListIds.length ? { buyerListIds: filters.buyerListIds } : {}),
          ...(filters.buyerCategoryIds.length
            ? { buyerCategoryIds: filters.buyerCategoryIds }
            : {}),
        }),
    ...(filters.jobTitles.length ? { jobTitles: filters.jobTitles } : {}),
    ...(filters.seniorityTiers.length ? { seniorityTiers: filters.seniorityTiers } : {}),
    ...(filters.jobFunctions.length ? { jobFunctions: filters.jobFunctions } : {}),
  };
}
